/**
 * BulkCreateOperationsPage - History page for bulk create operations
 *
 * Shows a list of past and current bulk create operations with their status and results.
 */

import { ArrowLeft, RefreshCw } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { OperationStatusBadge } from '@/components/external-task-bulk-create';
import { Badge } from '@/components/ui/catalyst/badge';
import { Button } from '@/components/ui/catalyst/button';
import { cn } from '@/lib/utils';
import {
  enhancedApi as externalTaskApi,
  type ExternalTaskBulkCreateItemSchema,
} from '@/services/api/generated/externalTaskService';

// Main list page
export const BulkCreateOperationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = React.useState<string | undefined>(
    undefined,
  );

  const {
    data: operations,
    isLoading,
    refetch,
  } = externalTaskApi.useExternalTaskBulkCreateListQuery({
    status: statusFilter,
  });

  const handleRowClick = (operationId: string) => {
    navigate(`/external-tasks/bulk-create-operations/${operationId}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div className="flex flex-col gap-6 px-10 py-8 h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link
            to="/external-tasks"
            className="inline-flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline mb-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to External Tasks
          </Link>
          <h1 className="text-3xl font-semibold text-zinc-900 dark:text-white">
            Bulk Create Operations
          </h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
            View history and status of bulk task creation operations
          </p>
        </div>
        <Button outline onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
          Status:
        </span>
        {['All', 'PENDING', 'RUNNING', 'COMPLETED', 'FAILED'].map((status) => {
          const isActive =
            statusFilter === (status === 'All' ? undefined : status);
          return isActive ? (
            <Button
              key={status}
              color="blue"
              onClick={() =>
                setStatusFilter(status === 'All' ? undefined : status)
              }
              className="text-sm"
            >
              {status}
            </Button>
          ) : (
            <Button
              key={status}
              plain
              onClick={() =>
                setStatusFilter(status === 'All' ? undefined : status)
              }
              className="text-sm"
            >
              {status}
            </Button>
          );
        })}
      </div>

      {/* Operations Table */}
      <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-sm border border-zinc-200 dark:border-zinc-800 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent" />
          </div>
        ) : !operations || operations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-zinc-500 dark:text-zinc-400">
            <p className="text-lg font-medium">No operations found</p>
            <p className="text-sm mt-1">
              Bulk create operations will appear here
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-zinc-50 dark:bg-zinc-800/50 border-b border-zinc-200 dark:border-zinc-700">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-600 dark:text-zinc-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-600 dark:text-zinc-400 uppercase tracking-wider">
                  Task Type
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-600 dark:text-zinc-400 uppercase tracking-wider">
                  Progress
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-600 dark:text-zinc-400 uppercase tracking-wider">
                  Results
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-600 dark:text-zinc-400 uppercase tracking-wider">
                  Initiated By
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-600 dark:text-zinc-400 uppercase tracking-wider">
                  Created
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {operations.map((operation) => (
                <tr
                  key={operation.id}
                  onClick={() => handleRowClick(operation.id)}
                  className="hover:bg-zinc-50 dark:hover:bg-zinc-800/50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    <OperationStatusBadge status={operation.status} />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-zinc-900 dark:text-white">
                      {operation.task_type_name}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-zinc-200 dark:bg-zinc-700 rounded-full overflow-hidden">
                        <div
                          className={cn(
                            'h-full rounded-full',
                            operation.status === 'COMPLETED' && 'bg-green-600',
                            operation.status === 'FAILED' && 'bg-red-600',
                            (operation.status === 'PENDING' ||
                              operation.status === 'RUNNING') &&
                              'bg-blue-600',
                          )}
                          style={{
                            width: `${operation.total_count > 0 ? ((operation.success_count + operation.skipped_count + operation.error_count) / operation.total_count) * 100 : 0}%`,
                          }}
                        />
                      </div>
                      <span className="text-xs text-zinc-500 dark:text-zinc-400">
                        {operation.success_count +
                          operation.skipped_count +
                          operation.error_count}
                        /{operation.total_count}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 text-xs">
                      {operation.success_count > 0 && (
                        <Badge color="green">
                          {operation.success_count} ok
                        </Badge>
                      )}
                      {operation.skipped_count > 0 && (
                        <Badge color="amber">
                          {operation.skipped_count} skip
                        </Badge>
                      )}
                      {operation.error_count > 0 && (
                        <Badge color="red">{operation.error_count} err</Badge>
                      )}
                      {operation.success_count === 0 &&
                        operation.skipped_count === 0 &&
                        operation.error_count === 0 && (
                          <span className="text-zinc-400">-</span>
                        )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-zinc-600 dark:text-zinc-400">
                      {operation.initiated_by_name || 'System'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-zinc-500 dark:text-zinc-400">
                      {formatDate(operation.created_at)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// Detail page for a single operation
export const BulkCreateOperationDetailPage: React.FC = () => {
  const { operationId } = useParams<{ operationId: string }>();
  const navigate = useNavigate();

  const { data: detail, isLoading } =
    externalTaskApi.useExternalTaskBulkCreateDetailQuery(
      { id: operationId || '' },
      {
        skip: !operationId,
        pollingInterval: 3000,
        skipPollingIfUnfocused: true,
      },
    );

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <p className="text-lg font-medium text-zinc-500">Operation not found</p>
        <Button
          plain
          onClick={() => navigate('/external-tasks/bulk-create-operations')}
          className="mt-4"
        >
          Back to Operations
        </Button>
      </div>
    );
  }

  const { operation, items } = detail;

  return (
    <div className="flex flex-col gap-6 px-10 py-8 h-full overflow-auto">
      {/* Header */}
      <div>
        <Link
          to="/external-tasks/bulk-create-operations"
          className="inline-flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Operations
        </Link>
        <div className="flex items-center gap-4">
          <h1 className="text-3xl font-semibold text-zinc-900 dark:text-white">
            {operation.task_type_name}
          </h1>
          <OperationStatusBadge status={operation.status} />
        </div>
        <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
          Started {formatDate(operation.created_at)} by{' '}
          {operation.initiated_by_name || 'System'}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white dark:bg-zinc-900 rounded-lg p-4 border border-zinc-200 dark:border-zinc-800">
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            Total Tasks
          </p>
          <p className="text-2xl font-semibold text-zinc-900 dark:text-white mt-1">
            {operation.total_count}
          </p>
        </div>
        <div className="bg-white dark:bg-zinc-900 rounded-lg p-4 border border-green-200 dark:border-green-800">
          <p className="text-sm text-green-600 dark:text-green-400">Created</p>
          <p className="text-2xl font-semibold text-green-600 dark:text-green-400 mt-1">
            {operation.success_count}
          </p>
        </div>
        <div className="bg-white dark:bg-zinc-900 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
          <p className="text-sm text-amber-600 dark:text-amber-400">Skipped</p>
          <p className="text-2xl font-semibold text-amber-600 dark:text-amber-400 mt-1">
            {operation.skipped_count}
          </p>
        </div>
        <div className="bg-white dark:bg-zinc-900 rounded-lg p-4 border border-red-200 dark:border-red-800">
          <p className="text-sm text-red-600 dark:text-red-400">Failed</p>
          <p className="text-2xl font-semibold text-red-600 dark:text-red-400 mt-1">
            {operation.error_count}
          </p>
        </div>
      </div>

      {/* Error message if any */}
      {operation.error_message && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm font-medium text-red-800 dark:text-red-200">
            Operation Error
          </p>
          <p className="text-sm text-red-600 dark:text-red-400 mt-1">
            {operation.error_message}
          </p>
        </div>
      )}

      {/* Items Table */}
      <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-sm border border-zinc-200 dark:border-zinc-800 overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-200 dark:border-zinc-700">
          <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">
            Task Items
          </h2>
        </div>
        <div className="overflow-auto max-h-[500px]">
          <table className="w-full">
            <thead className="bg-zinc-50 dark:bg-zinc-800/50 sticky top-0">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-600 dark:text-zinc-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-600 dark:text-zinc-400 uppercase tracking-wider">
                  External Task
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-600 dark:text-zinc-400 uppercase tracking-wider">
                  Result
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-600 dark:text-zinc-400 uppercase tracking-wider">
                  Processed
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {items.map((item) => (
                <ItemRow key={item.id} item={item} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Individual item row component
const ItemRow: React.FC<{ item: ExternalTaskBulkCreateItemSchema }> = ({
  item,
}) => {
  const getStatusBadge = () => {
    switch (item.status) {
      case 'SUCCESS':
        return <Badge color="green">Created</Badge>;
      case 'SKIPPED':
        return <Badge color="amber">Skipped</Badge>;
      case 'ERROR':
        return <Badge color="red">Failed</Badge>;
      case 'PENDING':
      default:
        return <Badge color="zinc">Pending</Badge>;
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <tr className="hover:bg-zinc-50 dark:hover:bg-zinc-800/50">
      <td className="px-4 py-3">{getStatusBadge()}</td>
      <td className="px-4 py-3">
        <span className="text-sm font-medium text-zinc-900 dark:text-white">
          {item.external_task_name}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-zinc-600 dark:text-zinc-400">
          {item.status === 'SUCCESS' && 'Internal task created'}
          {item.status === 'SKIPPED' && (item.skip_reason || 'Skipped')}
          {item.status === 'ERROR' && (item.error_message || 'Error occurred')}
          {item.status === 'PENDING' && 'Waiting to process...'}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-zinc-500 dark:text-zinc-400">
          {formatDate(item.processed_at)}
        </span>
      </td>
    </tr>
  );
};
